// resources/js/Pages/HallOfFame/utils/sorting.js
import { parseAvgScore } from './pcr';

export const sortScripts = (scripts, sortConfigOrColumn, directionArg) => {
    // Support both sortScripts(scripts, sortConfig) and sortScripts(scripts, column, direction)
    let column, direction;
    if (sortConfigOrColumn && typeof sortConfigOrColumn === 'object' && 'column' in sortConfigOrColumn) {
        column = sortConfigOrColumn.column;
        direction = sortConfigOrColumn.direction || 'desc';
    } else {
        column = sortConfigOrColumn;
        direction = directionArg || 'desc';
    }

    // If no sort column specified, preserve existing order (e.g. coverage sort from filters)
    if (!column) return scripts;

    const sorted = [...scripts].sort((a, b) => {
        let aValue, bValue;

        // Handle string columns
        if (column === 'title' || column === 'genre') {
            aValue = (a[column] || '').toLowerCase();
            bValue = (b[column] || '').toLowerCase();
            
            const comparison = aValue.localeCompare(bValue);
            return direction === 'desc' ? -comparison : comparison;
        }
        
        // Avg Score (0–10) sorting. Scripts with no score (older analyses that
        // only have P/C/R verdicts) always sink to the bottom, in either
        // direction — an unscored script is "unknown", not "worst".
        if (column === 'pcr_score') {
            const aScore = parseAvgScore(a.pcr_list).value;
            const bScore = parseAvgScore(b.pcr_list).value;

            if (aScore === null && bScore === null) return 0;
            if (aScore === null) return 1;
            if (bScore === null) return -1;

            return direction === 'desc' ? bScore - aScore : aScore - bScore;
        }

        // Handle PCR sorting by average rating number
        if (column === 'pcr') {
            // Parse PCR ratings to get numeric values
            const getPCRValue = (pcrData) => {
                if (!pcrData) return 0;
                
                // If it's an object (the actual PCR data structure)
                if (typeof pcrData === 'object' && !Array.isArray(pcrData)) {
                    let totalRating = 0;
                    let count = 0;
                    
                    Object.values(pcrData).forEach(data => {
                        if (data.screenplayRating) {
                            const rating = data.screenplayRating.substring(0, 3);
                            count++;
                            
                            if (rating === 'Pas') totalRating += 1;
                            else if (rating === 'Con') totalRating += 2;
                            else if (rating === 'Rec') totalRating += 3;
                            else if (rating === 'Hig') totalRating += 4;
                        }
                    });
                    
                    return count > 0 ? totalRating / count : 0;
                }
                
                // If it's a string (fallback for old format)
                if (typeof pcrData === 'string') {
                    const ratings = pcrData.split('|').map(rating => {
                        const trimmed = rating.trim();
                        if (trimmed === 'Pass') return 1;
                        if (trimmed === 'Consider') return 2;
                        if (trimmed === 'Recommend') return 3;
                        if (trimmed === 'Highly Recommend') return 4;
                        return 0;
                    });
                    
                    const sum = ratings.reduce((acc, val) => acc + val, 0);
                    return ratings.length > 0 ? sum / ratings.length : 0;
                }
                
                return 0;
            };
            
            aValue = getPCRValue(a.pcr_list);
            bValue = getPCRValue(b.pcr_list);
            
            return direction === 'desc' ? bValue - aValue : aValue - bValue;
        }
        
        // Percentile sorting (check percentiles first, then raw scores)
        if (a.percentiles && a.percentiles[column] !== undefined) {
            aValue = a.percentiles[column];
            bValue = b.percentiles[column];
        } else if (a.scores && a.scores[column] !== undefined) {
            // Fallback to raw scores if percentiles don't exist
            aValue = a.scores[column];
            bValue = b.scores[column];
        } else {
            // Direct property access
            aValue = a[column];
            bValue = b[column];
        }
        
        // Handle null/undefined values - push to end
        if (aValue === null || aValue === undefined || isNaN(aValue)) {
            return 1; // Push to end
        }
        if (bValue === null || bValue === undefined || isNaN(bValue)) {
            return -1; // Push to end
        }
        
        // Numeric comparison
        const numComparison = bValue - aValue; // Default descending
        return direction === 'desc' ? numComparison : -numComparison;
    });
    
    return sorted;
};
